/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Form, Row, Button } from "react-bootstrap";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import FormSelect from "../../components/Common/FormSelect";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import {
  createSupportTicketThunk,
  clearUpdateMessage,
  fetchConcernsThunk,
} from "../../redux/Slices/concernsListSlice";
import { useDispatch, useSelector } from "react-redux";
import { CATEGORY_TICKET, CONSTANTS } from "../../utils/Constant/Constant";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import ToastNotification from "../../components/Common/ToastNotification";
import Loader from "../../components/Common/Loader";
import appInsights from "../../AppInsights";

const TicketForm = ({ coachId, setIsFormOpen }) => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();

  const [toastConfig, setToastConfig] = useState(null);
  const [formData, setFormData] = useState({
    category: "",
    issueSubject: "",
    description: "",
    issueStatus: CONSTANTS.ONE,
    issueStatusReason: CONSTANTS.ONE,
    coachId: coachId,
  });

  const { updateSuccessMessage, updateFailedMessage, isloading } = useSelector(
    (state) => state.getConcerns
  );

  const [errors, setErrors] = useState({});

  // Track Page View
  useEffect(() => {
    appInsights.trackPageView({
      name: "Support_Ticket_Form_PageView",
      properties: { coachId },
    });
  }, [coachId]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Track Submit Click
    appInsights.trackEvent({
      name: "Support_Ticket_Submit_Clicked",
      properties: {
        coachId,
        category: formData.category,
      },
    });

    if (validateForm()) {
      dispatch(createSupportTicketThunk(formData)).then(() => {
        setFormData({
          category: "",
          issueSubject: "",
          description: "",
        });
        setErrors({});
        setTimeout(() => {
          setIsFormOpen(false);
          dispatch(fetchConcernsThunk(coachId));
        }, 1000);
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.category)
      newErrors.category = trans("concerns.categoryRequired");
    if (!formData.issueSubject)
      newErrors.issueSubject = trans("concerns.subjectRequired");
    if (!formData.description)
      newErrors.description = trans("concerns.descriptionRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === CONSTANTS.ZERO;
  };

  useEffect(() => {
    if (updateSuccessMessage) {
      // Track Success
      appInsights.trackEvent({
        name: "Support_Ticket_Submitted_Success",
        properties: { coachId },
      });

      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: trans("concerns.ticketSubmittedMsg"),
        heading: trans("Common.success"),
      });
      setTimeout(() => {
        dispatch(clearUpdateMessage());
        setToastConfig(null);
      }, 3000);
    } else if (updateFailedMessage) {
      // Track Failure
      appInsights.trackEvent({
        name: "Support_Ticket_Submitted_Failed",
        properties: {
          coachId,
          errorMessage: updateFailedMessage,
        },
      });

      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: updateFailedMessage,
        heading: trans("Common.failed"),
      });
      setTimeout(() => {
        dispatch(clearUpdateMessage());
        setToastConfig(null);
      }, 3000);
    }
  }, [updateSuccessMessage, updateFailedMessage, trans, dispatch]);

  return isloading ? (
    <Loader message="" />
  ) : (
    <div className=" ticket-form p-4">
      <div className="d-flex justify-content-between align-items-center">
        <h4>{trans("concerns.fileNewTicket")}</h4>
        <Button
          className="create-button"
          onClick={() => {
            //Track Close Click
            appInsights.trackEvent({
              name: "Support_Ticket_Form_Closed",
              properties: { coachId },
            });

            setIsFormOpen(false);
          }}
        >
          <i className=" bi bi-x-lg"></i>
        </Button>
      </div>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-1">
          <Form.Group controlId="category">
            <FormLabel label="Category" required />
            <FormSelect
              name="category"
              value={formData.category}
              onChange={handleFieldChange}
              options={CATEGORY_TICKET}
              required
              isInvalid={!!errors.category}
              feedback={errors.category}
            />
          </Form.Group>
        </Row>
        <Row className="mb-1">
          <Form.Group controlId="issueSubject">
            <FormLabel label="Subject" required />
            <FormControl
              type="text"
              name="issueSubject"
              value={formData.issueSubject}
              onChange={handleFieldChange}
              required
              isInvalid={!!errors.issueSubject}
              feedback={errors.issueSubject}
            />
          </Form.Group>
        </Row>
        <Row className="mb-1">
          <Form.Group controlId="description">
            <FormLabel label="Description" required />
            <FormControlTextArea
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              required
              isInvalid={!!errors.description}
              feedback={errors.description}
            />
          </Form.Group>
        </Row>
        <Button className="create-button" type="submit">
          {trans("concerns.submit")}
        </Button>
      </Form>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default TicketForm;
